import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mic, Play, Save, KeyRound, Volume2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { UnderConstructionButton } from './UnderConstruction';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const OPENAI_VOICES = ['nova', 'onyx', 'echo', 'shimmer', 'alloy', 'fable', 'sage', 'coral', 'ash'];

const SAMPLE = 'Hola equipo, soy Noah. Hoy cerramos una venta y hay cinco conversaciones esperando respuesta.';

export const NoahVoiceSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [voices, setVoices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/noah/voice-config`);
      setCfg(data);
      if (data.elevenlabs_ready) {
        try {
          const r = await axios.get(`${API}/noah/voices`);
          setVoices(r.data.voices || []);
        } catch { setVoices([]); }
      }
    } catch { /* silencioso */ }
  };

  useEffect(() => { load(); }, []);

  if (!cfg) return null;

  const save = async (patch) => {
    setSaving(true);
    try {
      const body = {
        provider: patch.provider ?? cfg.provider,
        voice_id: patch.voice_id ?? cfg.voice_id,
        voice_name: patch.voice_name ?? cfg.voice_name,
        openai_voice: patch.openai_voice ?? cfg.openai_voice,
      };
      const { data } = await axios.put(`${API}/noah/voice-config`, body);
      setCfg(data);
      toast.success('Voz de Noah actualizada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No pude guardar la voz');
    } finally { setSaving(false); }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const { data } = await axios.post(`${API}/noah/speak`, { text: SAMPLE, voice: cfg.openai_voice || 'nova' });
      const audio = new Audio(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      await audio.play();
      toast.success(data.provider === 'elevenlabs' ? 'Escuchando ElevenLabs' : 'Escuchando voz de respaldo');
    } catch (e) {
      toast.error('No pude generar la prueba de voz');
    } finally { setPreviewing(false); }
  };

  return (
    <div className="space-y-5" data-testid="noah-voice-settings">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold flex items-center gap-2">
            <Mic className="w-4 h-4 text-brand-copper" /> Voz de Noah
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Se usa en el resumen matutino por WhatsApp y en el botón "Escuchar" del dashboard.
          </p>
        </div>
        <Badge data-testid="noah-voice-active" variant="outline" className="shrink-0 text-[10px]">
          {cfg.active === 'elevenlabs' ? 'ElevenLabs' : 'Voz de respaldo'}
        </Badge>
      </div>

      {!cfg.elevenlabs_ready && (
        <div className="rounded-xl border border-brand-copper/40 bg-brand-butter/20 p-3" data-testid="noah-voice-key-missing">
          <p className="text-xs font-semibold flex items-center gap-1.5">
            <KeyRound className="w-3.5 h-3.5" /> Falta la API key de ElevenLabs
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Crea tu llave en elevenlabs.io → Settings → API Keys y pídele al equipo que la pegue en el servidor
            (<span className="font-mono">ELEVENLABS_API_KEY</span>). Mientras tanto Noah sigue hablando con la voz de respaldo.
          </p>
          <div className="mt-2">
            <UnderConstructionButton
              label="Activar voz natural (ElevenLabs)"
              detail="Todo el código está listo: en cuanto pegues la API key de ElevenLabs en el servidor, este botón activará las voces en español natural."
              testid="elevenlabs-activate-btn"
              icon={KeyRound}
            />
          </div>
        </div>
      )}

      {cfg.elevenlabs_ready && (
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Proveedor de voz</Label>
            <Select value={cfg.provider} onValueChange={v => save({ provider: v })}>
              <SelectTrigger data-testid="noah-voice-provider"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="elevenlabs">ElevenLabs (voz natural)</SelectItem>
                <SelectItem value="openai">Voz de respaldo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {cfg.provider === 'elevenlabs' && (
            <div>
              <Label className="text-xs">Voz ({voices.length} disponibles en tu cuenta)</Label>
              <Select value={cfg.voice_id || ''}
                onValueChange={v => save({ voice_id: v, voice_name: voices.find(x => x.voice_id === v)?.name || '' })}>
                <SelectTrigger data-testid="noah-voice-select"><SelectValue placeholder="Elige una voz" /></SelectTrigger>
                <SelectContent>
                  {voices.map(v => (
                    <SelectItem key={v.voice_id} value={v.voice_id}>
                      {v.name}{v.language ? ` · ${v.language}` : ''}{v.gender ? ` · ${v.gender}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      <div>
        <Label className="text-xs">Voz de respaldo (si ElevenLabs falla o se queda sin cuota)</Label>
        <Select value={cfg.openai_voice || 'nova'} onValueChange={v => save({ openai_voice: v })}>
          <SelectTrigger data-testid="noah-fallback-voice"><SelectValue /></SelectTrigger>
          <SelectContent>
            {OPENAI_VOICES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <Button data-testid="noah-voice-preview" variant="outline" size="sm" className="gap-1.5" onClick={preview} disabled={previewing}>
          <Play className="w-3.5 h-3.5" /> Escuchar prueba
        </Button>
        <Button data-testid="noah-voice-save" size="sm" className="gap-1.5" onClick={() => save({})} disabled={saving}>
          <Save className="w-3.5 h-3.5" /> Guardar
        </Button>
        <span className="text-[11px] text-muted-foreground flex items-center gap-1 ml-auto">
          <Volume2 className="w-3 h-3" /> {SAMPLE.slice(0, 22)}…
        </span>
      </div>
    </div>
  );
};
